import type { TelegramWidgetData } from '@ielts-buddy/shared'
import { useEffect, useRef } from 'react'
import { useAuthStore } from '../shared/auth-store.js'
import { LoadingDots } from '../shared/ui/LoadingDots.js'
import { LogoMark } from '../shared/ui/LogoMark.js'

declare global {
  interface Window {
    onTelegramAuth?: (data: TelegramWidgetData) => void
  }
}

export function LoginScreen() {
  const { status, error, login, widgetResetNonce } = useAuthStore()
  const widgetRef = useRef<HTMLDivElement>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: widgetResetNonce intentionally remounts widget after logout to reset Telegram login button state
  useEffect(() => {
    window.onTelegramAuth = async (data: TelegramWidgetData) => {
      await login(data)
    }

    const botUsername = import.meta.env.VITE_TELEGRAM_BOT_USERNAME
    if (!botUsername || !widgetRef.current) return

    widgetRef.current.replaceChildren()

    const script = document.createElement('script')
    script.src = 'https://telegram.org/js/telegram-widget.js?22'
    script.setAttribute('data-telegram-login', botUsername)
    script.setAttribute('data-size', 'large')
    script.setAttribute('data-onauth', 'onTelegramAuth(user)')
    script.setAttribute('data-request-access', 'write')
    script.async = true

    widgetRef.current.appendChild(script)

    return () => {
      delete window.onTelegramAuth
      widgetRef.current?.replaceChildren()
    }
  }, [login, widgetResetNonce])

  return (
    <div className="h-[100dvh] overflow-hidden flex items-center justify-center bg-brand-bg p-6">
      <div className="w-full max-w-sm animate-fade-in">
        {/* Logo / brand mark */}
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-brand-gradient-hero flex items-center justify-center shadow-brand-hero">
            <LogoMark size={32} />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-brand-text text-center mb-2">
          IELTS Buddy
        </h1>
        <p className="text-brand-text-muted text-center text-sm mb-8">
          Centre Dashboard — track your students' progress and earnings
        </p>

        {status === 'loading' ? (
          <div className="flex justify-center py-4">
            <LoadingDots />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div ref={widgetRef} />
            {!import.meta.env.VITE_TELEGRAM_BOT_USERNAME && (
              <p className="text-xs text-brand-warning text-center">
                VITE_TELEGRAM_BOT_USERNAME is not set. Add it to your .env file.
              </p>
            )}
          </div>
        )}

        {error && (
          <p className="mt-4 text-sm text-brand-error text-center">{error}</p>
        )}
      </div>
    </div>
  )
}
